import { useCallback, useState } from 'react';
import { alpacaBackendService, OrderRequest, AlpacaCredentials } from '@/services/alpacaBackendService';
import { useToast } from '@/hooks/use-toast';

export function useAlpacaBackend() {
  const { toast } = useToast?.() || { toast: (x: any) => {} };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wrap = useCallback(async <T,>(fn: () => Promise<T>, successMsg?: string): Promise<T | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fn();
      if (successMsg && toast) toast({ title: successMsg });
      return res;
    } catch (e: any) {
      const message = e?.message || 'Unknown error';
      setError(message);
      if (toast) toast({ title: 'Alpaca Backend Error', description: message, variant: 'destructive' });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  return {
    loading,
    error,
    getAccount: () => wrap(() => alpacaBackendService.getAccount()),
    setAlpacaCredentials: (creds: AlpacaCredentials) => wrap(() => alpacaBackendService.setAlpacaCredentials(creds), 'Credentials saved'),
    getAlpacaCredentials: () => wrap(() => alpacaBackendService.getAlpacaCredentials()),
    buyStock: (order: OrderRequest) => wrap(() => alpacaBackendService.buyStock(order), 'Buy order placed'),
    sellStock: (order: OrderRequest) => wrap(() => alpacaBackendService.sellStock(order), 'Sell order placed'),
    getOrders: (accountId?: string) => wrap(() => alpacaBackendService.getOrders(accountId)),
    getOrder: (orderId: string) => wrap(() => alpacaBackendService.getOrder(orderId)),
    cancelOrder: (accountId: string, orderId: string) => wrap(() => alpacaBackendService.cancelOrder(accountId, orderId), 'Order cancelled'),
    getPositions: (accountId?: string) => wrap(() => alpacaBackendService.getPositions(accountId)),
    getPosition: (symbol: string) => wrap(() => alpacaBackendService.getPosition(symbol)),
    closePosition: (accountId: string, symbol: string) => wrap(() => alpacaBackendService.closePosition(accountId, symbol), 'Position closed'),
    getTrades: (params?: Record<string, string>) => wrap(() => alpacaBackendService.getTrades(params)),
    getAlpacaTrades: (params?: Record<string, string>) => wrap(() => alpacaBackendService.getAlpacaTrades(params)),
    getSupabaseTrades: (params?: Record<string, string>) => wrap(() => alpacaBackendService.getSupabaseTrades(params)),
  };
}
